import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { AlertTriangle, RotateCcw, ExternalLink, CheckCircle, AlertCircle, HelpCircle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { bankrollApi } from '../../../api/client';
import { useAppStore } from '../../../store/useAppStore';
import { formatCurrency, formatPct } from '../../../utils/format';

type ActionStatus = 'idle' | 'saving' | 'success' | 'error';

const STAKING_TOOLTIPS: Record<string, string> = {
  flat:          'Fixed dollar stake on every bet, regardless of edge or confidence.',
  kelly:         'Full Kelly criterion — sizes stake to maximize long-run geometric growth. High variance.',
  half_kelly:    '50% of Kelly stake. Preserves edge advantage while halving variance.',
  quarter_kelly: '25% of Kelly stake. Conservative sizing ideal for building a track record.',
};

// ── Exposure arc gauge (mini) ─────────────────────────────────────────────────

function MiniArcGauge({ pct }: { pct: number }) {
  const t = Math.min(Math.max(pct, 0), 1);
  const strokeColor = t >= 0.20 ? '#ef4444' : t >= 0.10 ? '#f59e0b' : '#3b82f6';
  const r = 24, cx = 32, cy = 30, sw = 5;
  const toXY = (frac: number) => ({
    x: +(cx + r * Math.cos(Math.PI * (1 + frac))).toFixed(2),
    y: +(cy + r * Math.sin(Math.PI * (1 + frac))).toFixed(2),
  });
  const bgEnd = toXY(1);
  const bgPath = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${bgEnd.x} ${bgEnd.y}`;
  const fp = toXY(t);
  const fillPath = t > 0.005 ? `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${fp.x} ${fp.y}` : '';
  return (
    <svg width="64" height="38" viewBox="0 0 64 38">
      <path d={bgPath} fill="none" stroke="#1e2d3d" strokeWidth={sw} strokeLinecap="round" />
      {fillPath && <path d={fillPath} fill="none" stroke={strokeColor} strokeWidth={sw} strokeLinecap="round" />}
      <text x={cx} y={cy - 2} textAnchor="middle" fill="#e5e7eb" fontSize="9" fontWeight="bold" fontFamily="monospace">
        {Math.round(t * 100)}%
      </text>
    </svg>
  );
}

// ── Violations blocking modal ─────────────────────────────────────────────────

function ViolationsModal({ violations, onAcknowledge }: { violations: string[]; onAcknowledge: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-surface-1 border border-loss/40 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-loss/10 border border-loss/20">
            <AlertTriangle className="w-5 h-5 text-loss" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-loss">Risk Rule Violations Detected</h3>
            <p className="text-[11px] text-gray-500">Resolve or acknowledge before proceeding</p>
          </div>
        </div>
        <div className="space-y-2">
          {violations.map((v, i) => (
            <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-loss/5 border border-loss/15">
              <span className="w-1.5 h-1.5 rounded-full bg-loss flex-shrink-0 mt-1.5" />
              <span className="text-xs text-loss/80">{v}</span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-gray-500">
          These violations mean your current bets or settings exceed your configured risk limits. Review and adjust your positions or update your risk controls.
        </p>
        <button
          onClick={onAcknowledge}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-loss/10 text-loss border border-loss/30 rounded-lg hover:bg-loss/20 transition-colors"
        >
          <X className="w-4 h-4" />
          I Acknowledge — Proceed
        </button>
      </div>
    </div>
  );
}

export function WalletSettingsPage() {
  const { bankrollSettings: bs, exposure, fetchBankroll, updateBankrollSettings, bets } = useAppStore();

  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<ActionStatus>('idle');
  const [violationsAcknowledged, setViolationsAcknowledged] = useState(false);

  const [resetAmount, setResetAmount] = useState('');
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    fetchBankroll();
  }, []);

  useEffect(() => {
    fetchBankroll();
  }, [bets]);

  const startEdit = () => {
    if (!bs) return;
    setDraft({
      unitSize: bs.unitSize.toString(),
      stakingMethod: bs.stakingMethod,
      maxDailyLoss: bs.maxDailyLoss.toString(),
      maxBetSize: bs.maxBetSize.toString(),
      maxExposurePerGame: bs.maxExposurePerGame.toString(),
      maxTotalExposure: bs.maxTotalExposure.toString(),
    });
    setEditMode(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaveStatus('idle');
    try {
      await updateBankrollSettings({
        unitSize: parseFloat(draft.unitSize),
        stakingMethod: draft.stakingMethod as 'flat' | 'kelly' | 'half_kelly' | 'quarter_kelly',
        maxDailyLoss: parseFloat(draft.maxDailyLoss),
        maxBetSize: parseFloat(draft.maxBetSize),
        maxExposurePerGame: parseFloat(draft.maxExposurePerGame),
        maxTotalExposure: parseFloat(draft.maxTotalExposure),
      });
      setEditMode(false);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const amount = parseFloat(resetAmount);
    if (!amount || amount <= 0) return;
    setResetting(true);
    try {
      await bankrollApi.reset(amount);
      await fetchBankroll();
      setResetAmount('');
    } finally {
      setResetting(false);
    }
  };

  if (!bs) return null;

  const confirmReset = async () => {
    setShowResetConfirm(false);
    await handleReset();
  };

  const pnl = bs.currentBankroll - bs.startingBankroll;
  const roi = bs.startingBankroll > 0 ? pnl / bs.startingBankroll : 0;
  const dailyLossAtRisk = (exposure?.dailyLoss ?? 0) > (exposure?.dailyLossLimit ?? Infinity) * 0.8;
  const exposurePct = exposure?.percentOfBankroll ?? 0;
  const violations = exposure?.violations ?? [];

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
      {/* Blocking violations modal */}
      {violations.length > 0 && !violationsAcknowledged && (
        <ViolationsModal violations={violations} onAcknowledge={() => setViolationsAcknowledged(true)} />
      )}

      {/* Reset confirmation modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-surface-1 border border-warn/40 rounded-xl shadow-2xl max-w-sm w-full mx-4 p-6 space-y-4">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-warn/10 border border-warn/20">
                <AlertTriangle className="w-5 h-5 text-warn" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-warn">Confirm Bankroll Reset</h3>
                <p className="text-[11px] text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-xs text-gray-300">
              This will set your bankroll to{' '}
              <span className="font-mono font-bold text-gray-100">${parseFloat(resetAmount || '0').toLocaleString()}</span>.
              All bet history is preserved, but your current balance will be overwritten.
            </p>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex-1 px-3 py-2 text-xs font-medium rounded-lg bg-surface-3 border border-surface-4 text-gray-300 hover:bg-surface-4 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmReset}
                disabled={resetting}
                className="flex-1 btn-danger text-xs py-2"
              >
                {resetting ? 'Resetting...' : 'Confirm Reset'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold text-gray-100">Wallet</h2>
        <p className="text-sm text-gray-500 mt-1">Manage your bankroll, deposits, and risk controls.</p>
      </div>

      {/* Balance Overview */}
      <div className="op-panel p-4">
        <h3 className="text-sm font-medium text-gray-300 mb-4">Balance Overview</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="stat-label">Current Bankroll</div>
            <div className="text-2xl font-bold font-mono text-gray-100 mt-1">
              {formatCurrency(bs.currentBankroll)}
            </div>
            <div className={clsx('text-xs mt-1', pnl >= 0 ? 'text-win' : 'text-loss')}>
              {pnl >= 0 ? '+' : ''}{formatCurrency(pnl)} ({formatPct(Math.abs(roi))})
            </div>
          </div>

          <div>
            <div className="stat-label">Open Exposure</div>
            <div className="flex items-center gap-2 mt-1">
              <MiniArcGauge pct={exposurePct} />
              <div>
                <div className={clsx('text-lg font-bold font-mono', exposurePct >= 0.20 ? 'text-loss' : exposurePct >= 0.10 ? 'text-warn' : 'text-gray-100')}>
                  {formatCurrency(exposure?.totalOpen ?? 0)}
                </div>
                <div className="text-xs text-gray-500">of bankroll</div>
              </div>
            </div>
          </div>

          <div>
            <div className="stat-label">Potential Return</div>
            <div className="text-2xl font-bold font-mono text-win mt-1">
              {formatCurrency(exposure?.totalPotentialReturn ?? 0)}
            </div>
            <div className="text-xs text-gray-500 mt-1">If all open bets win</div>
          </div>

          <div>
            <div className="stat-label">Daily Loss Used</div>
            <div className={clsx('text-2xl font-bold font-mono mt-1', dailyLossAtRisk ? 'text-loss' : 'text-gray-100')}>
              {formatCurrency(exposure?.dailyLoss ?? 0)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Limit: {formatCurrency(exposure?.dailyLossLimit ?? 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Transactions → redirect to Bankroll page */}
      <div className="op-panel p-4 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-1">Deposits &amp; Withdrawals</h3>
          <p className="text-xs text-gray-500">
            Manage your bankroll deposits and withdrawals directly from the Bankroll operations page.
          </p>
        </div>
        <Link
          to="/baseball/mlb/bankroll"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-surface-3 text-gray-200 border border-surface-4 rounded-md hover:bg-surface-4 transition-colors flex-shrink-0"
        >
          <ExternalLink className="w-3 h-3" />
          Open Bankroll
        </Link>
      </div>

      {/* Risk Controls */}
      <div className="op-panel p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-300">Risk Controls</h3>
          {!editMode ? (
            <button onClick={startEdit} className="btn-ghost text-xs py-1.5">Edit</button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => setEditMode(false)} className="btn-ghost text-xs py-1.5">Cancel</button>
              <button onClick={saveSettings} disabled={saving} className="btn-primary text-xs py-1.5">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-5">
          {editMode ? (
            <>
              <SettingField label="Unit Size ($)" value={draft.unitSize} onChange={(v) => setDraft({ ...draft, unitSize: v })} />
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Staking Method</label>
                <select
                  value={draft.stakingMethod}
                  onChange={(e) => setDraft({ ...draft, stakingMethod: e.target.value })}
                  className="w-full px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                >
                  <option value="flat">Flat</option>
                  <option value="kelly">Full Kelly</option>
                  <option value="half_kelly">Half Kelly</option>
                  <option value="quarter_kelly">Quarter Kelly</option>
                </select>
              </div>
              <SettingField label="Max Daily Loss ($)" value={draft.maxDailyLoss} onChange={(v) => setDraft({ ...draft, maxDailyLoss: v })} />
              <SettingField label="Max Bet Size ($)" value={draft.maxBetSize} onChange={(v) => setDraft({ ...draft, maxBetSize: v })} />
              <SettingField label="Max Exposure/Game ($)" value={draft.maxExposurePerGame} onChange={(v) => setDraft({ ...draft, maxExposurePerGame: v })} />
              <SettingField label="Max Total Exposure ($)" value={draft.maxTotalExposure} onChange={(v) => setDraft({ ...draft, maxTotalExposure: v })} />
            </>
          ) : (
            <>
              <SettingDisplay label="Unit Size" value={formatCurrency(bs.unitSize)} />
              <div>
                <div className="flex items-center gap-1 mb-0.5">
                  <span className="stat-label">Staking Method</span>
                  <button
                    type="button"
                    title={STAKING_TOOLTIPS[bs.stakingMethod] ?? ''}
                    className="text-gray-600 hover:text-gray-400 transition-colors"
                  >
                    <HelpCircle className="w-3 h-3" />
                  </button>
                </div>
                <div className="text-sm font-semibold text-gray-200 mt-0.5 capitalize">{bs.stakingMethod.replace(/_/g, ' ')}</div>
                <div className="text-[10px] text-gray-600 mt-0.5 leading-tight">{STAKING_TOOLTIPS[bs.stakingMethod]}</div>
              </div>
              <SettingDisplay label="Max Daily Loss" value={formatCurrency(bs.maxDailyLoss)} />
              <SettingDisplay label="Max Bet Size" value={formatCurrency(bs.maxBetSize)} />
              <SettingDisplay label="Max Exposure/Game" value={formatCurrency(bs.maxExposurePerGame)} />
              <SettingDisplay label="Max Total Exposure" value={formatCurrency(bs.maxTotalExposure)} />
            </>
          )}
        </div>

        {saveStatus === 'success' && (
          <span className="flex items-center gap-1.5 text-sm text-win mt-4">
            <CheckCircle className="w-4 h-4" /> Saved
          </span>
        )}
        {saveStatus === 'error' && (
          <span className="flex items-center gap-1.5 text-sm text-loss mt-4">
            <AlertCircle className="w-4 h-4" /> Failed to save
          </span>
        )}
      </div>

      {/* Reset Bankroll */}
      <div className="op-panel p-4 border-warn/20">
        <div className="flex items-center gap-2 mb-1">
          <RotateCcw className="w-4 h-4 text-warn" />
          <h3 className="text-sm font-medium text-gray-300">Reset Bankroll</h3>
        </div>
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-3.5 h-3.5 text-warn" />
          <p className="text-xs text-warn">Sets a new starting amount and resets current balance. Bet history is preserved.</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="number"
            placeholder="New starting amount..."
            value={resetAmount}
            onChange={(e) => setResetAmount(e.target.value)}
            className="flex-1 px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-warn"
          />
          <button
            onClick={() => setShowResetConfirm(true)}
            disabled={!resetAmount || resetting}
            className="btn-danger"
          >
            {resetting ? 'Resetting...' : 'Reset'}
          </button>
        </div>
        <p className="text-xs text-gray-600 mt-2">
          Current: {formatCurrency(bs.currentBankroll)} · Started at {formatCurrency(bs.startingBankroll)}
        </p>
      </div>
    </div>
  );
}

function SettingDisplay({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className="text-sm font-semibold text-gray-200 mt-0.5">{value}</div>
    </div>
  );
}

function SettingField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1.5">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
      />
    </div>
  );
}
